<template>
    <div class="summary-container">
        <div
            v-for="summary in stageSummaries"
            :key="summary.stage"
            class="summary-item"
        >
            <div class="summary-count">{{ summary.count }}</div>
            <div class="summary-content">
                <span class="summary-text">{{ getSummaryText(summary) }}</span>
                <Tag
                    v-if="summary.nextStage"
                    :class="getStageTagClass(summary.nextStage)"
                    :value="summary.nextStage"
                />
                <span
                    v-else
                    class="no-next-stage"
                    >-</span
                >
            </div>
        </div>
    </div>
</template>

<script>
import Tag from 'primevue/tag'
import CoreIcon from '@/components/core/CoreIcon.vue'

export default {
    name: 'ConfirmBulkSendModalSummary',

    components: {
        Tag,
        CoreIcon,
    },

    props: {
        invoices: {
            type: Array,
            required: true,
            default: () => [],
        },
        stagesMap: {
            type: Object,
            required: true,
            default: () => ({}),
        },
    },

    computed: {
        stageSummaries() {
            const grouped = this.invoices.reduce((acc, invoice) => {
                const stage = invoice.current_stage_name
                if (!acc[stage]) {
                    acc[stage] = {
                        stage,
                        count: 0,
                        nextStage: this.stagesMap[stage]?.nextStage || null,
                        action: this.stagesMap[stage]?.action || null,
                    }
                }
                acc[stage].count++
                return acc
            }, {})

            return Object.values(grouped).sort((a, b) => b.count - a.count)
        },
    },

    methods: {
        getStageTagClass(stage) {
            const stageClasses = {
                created: 'stage-created',
                dispatched: 'stage-dispatched',
                reminder: 'stage-reminder',
                warning_1: 'stage-warning',
                warning_2: 'stage-warning',
                paid: 'stage-paid',
                debt_collection: 'stage-debt',
                canceled: 'stage-canceled',
            }

            return stageClasses[stage] || ''
        },

        getSummaryText(summary) {
            const plural =
                summary.count > 1
                    ? '§invoices.invoices_plural_to_be'
                    : '§invoices.invoice_singular_to_be'
            return `${summary.stage} ${this.$t(plural)}`
        },
    },
}
</script>

<style scoped>
.summary-container {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
    background: var(--color-neutral-500);
    overflow: hidden;
    margin-bottom: 4px;
    padding: 0.6rem 0;
    border-radius: 4px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    font-size: 1.3rem;
    color: var(--text-dark);
    transition: background-color 0.2s ease;
}

.summary-count {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    font-weight: 700;
    font-size: 1.4rem;
    color: white;
    background: linear-gradient(135deg, var(--color-primary-600) 0%, var(--color-primary-700) 100%);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.summary-text {
    font-weight: 400;
    color: var(--text-dark);
}

.no-next-stage {
    font-weight: 400;
    color: var(--color-neutral-500);
    font-size: 1.3rem;
}

:deep(.p-tag) {
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 1.3rem;
    font-weight: 400;
}

:deep(.stage-created) {
    background-color: var(--color-neutral-400);
    color: var(--text-dark);
}

:deep(.stage-dispatched) {
    background-color: var(--color-primary-500);
    color: var(--text-dark);
}

:deep(.stage-reminder) {
    background-color: var(--color-red-300);
    color: var(--text-dark);
}

:deep(.stage-warning) {
    background-color: var(--color-red-500);
    color: var(--text-dark);
}

:deep(.stage-paid) {
    background-color: var(--color-primary-800);
    color: var(--text-dark);
}

:deep(.stage-debt) {
    background-color: var(--color-secondary-900);
    color: var(--text-dark);
}

:deep(.stage-canceled) {
    background-color: var(--color-neutral-700);
    color: var(--text-dark);
}
</style>
